<template>
  <div class="list-page">
    <div v-if="!noHead" class="list-head">
      <div class="title">{{ titleLabel }}</div>
      <div v-if="!noFilter" class="filter-container">
        <slot name="filter"></slot>
      </div>
      <div v-if="!noHeadBtn" class="btn-container">
        <slot name="btn-before"></slot>
        <slot name="btn">
          <el-button v-if="showCreateBtn" class="create-btn" @click="handleCreate">{{ defaultCreateBtnLabel }}</el-button>
        </slot>
        <slot name="btn-after"></slot>
      </div>
    </div>
    <div v-if="showSearchFilter" class="search-filter">
      <div class="search-filter-container">
        <slot name="search-filter"></slot>
      </div>
      <div class="search-filter-btn">
        <slot name="search-filter-btn"></slot>
      </div>
    </div>
    <div class="list-container">
      <el-table
        v-rect-loading="loading"
        ref="elTable"
        tooltip-effect="light"
        :height="tableHeight"
        :data="listData"
        :row-key="rowKey"
        :row-class-name="rowClassName"
        :cell-style="cellStyle"
        :tree-props="treeProps"
        :default-expand-all="defaultExpandAll"
        :expand-row-keys="expandRowKeys"
        :default-sort="defaultSort"
        :span-method="spanMethod"
        @sort-change="handleSortChange"
        @select-all="handleSelectAll"
        @selection-change="handleSelectionChange"
        @expand-change="handleExpandChange"
      >
        <slot>
          <template v-for="(item, index) in tableColumns">
            <el-table-column
              v-if="!item.hidden"
              :key="index"
              :type="item.columnType"
              :class-name="item.cellClass"
              :label="selectLabelI18n(item)"
              :prop="item.dataKey"
              :fixed="item.fixed"
              :align="item.align"
              :disabled="item.disabled"
              :min-width="item.width"
              :sortable="item.sort && item.sort.enable"
              :sort-method="item.sort && item.sort.handler"
              :sort-by="item.sort && item.sort.by"
              :sort-orders="item.sort && item.sort.orders"
              :filters="item.filter && item.filter.options"
              :filter-method="item.filter && item.filter.handler"
              :show-overflow-tooltip="getColumnShowOverflowTooltip(item)"
            >
              <template v-if="item.headerSlotName" #header="{ column, $index }">
                <slot v-bind="{ column, $index }" :name="item.headerSlotName"></slot>
              </template>
              <template v-if="item.isDragSort" #default="{ row, column, $index }">
                <slot v-bind="{ column, $index }" :name="item.slotName" :row="row">
                  <i class="drag-sort-icon" :class="[dragSortIcon, dragElClass]"></i>
                </slot>
              </template>
              <template v-else-if="item.dataKey" #default="{ row, column, $index }">
                <slot
                  v-if="item.slotName"
                  v-bind="{ column, $index }"
                  :name="item.slotName"
                  :dataKey="item.dataKey"
                  :row="row"
                  :data="getColumnData(item, row, column, $index)"
                  :formattedData="getFormattedColumnData(item, row, column, $index)"
                >
                  {{ getFormattedColumnData(item, row, column, $index) }}
                </slot>
                <template v-else>{{ getFormattedColumnData(item, row, column, $index) }}</template>
              </template>
            </el-table-column>
          </template>
        </slot>
        <el-table-column v-if="!noAction" class-name="action-btn-column" :min-width="actionWidth" :label="actionLabel">
          <template #default="{ row, $index }">
            <slot name="action-btn" v-bind="{ row, $index }">
              <el-dropdown
                v-if="getActionBtnShow(row, $index)"
                class="action-dropdown"
                trigger="click"
                :hide-on-click="hideOnClick"
                :disabled="getActionBtnDisabled(row, $index)"
                @command="handleAction($event, row, $index)"
              >
                <el-button v-if="getActionBtnTooltipShow(row, $index)" v-auto-tooltip.always="getActionBtnTooltip(row, $index)" class="dropdown-btn" :disabled="getActionBtnDisabled(row, $index)">
                  <span>{{ actionLabel }}</span>
                  <i class="dropdown-btn-icon el-icon-more"></i>
                </el-button>
                <el-button v-else class="dropdown-btn" :disabled="getActionBtnDisabled(row, $index)">
                  <span>{{ actionLabel }}</span>
                  <i class="dropdown-btn-icon el-icon-more"></i>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu class="no-arrow-dropdown-menu">
                    <template v-for="(btn, key) in mergedActionBtn">
                      <el-dropdown-item
                        v-if="getActionItemBtnShow(btn, key, row, $index)"
                        :key="key"
                        :command="{ btn, btnName: key }"
                      >{{ getActionBtnLabel(btn, key, row, $index) }}</el-dropdown-item>
                    </template>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </slot>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="!noPagination && pagination"
        :layout="pagination.layout || 'total, sizes, prev, pager, next, jumper'"
        :current-page="pagination.page"
        :page-sizes="pagination.pageSizes || [10, 50, 100, 500]"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :pager-count="pagination.pagerCount"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </div>
    <slot name="page-bottom"></slot>
  </div>
</template>

<script>
import _ from 'lodash'
import { selectFuncLabelI18n, selectLabelI18n } from '@/i18n'
import { formatBigNumber } from '@/components/filters'
import Sortable from 'sortablejs'

export default {
  metaInfo () {
    return {
      style: [
        {
          innerHTML: `.el-tooltip__popper.is-light {
            border-color: #e6e6e6;
            background: #e6e6e6;
            -webkit-box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
                    box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
          }
          .el-tooltip__popper.is-light[x-placement] .popper__arrow {
            border-bottom-color: #e6e6e6;
            border-top-color: #e6e6e6;
          }
          .el-tooltip__popper.is-light[x-placement] .popper__arrow::after {
            border-bottom-color: #e6e6e6;
            border-top-color: #e6e6e6;
          }`
        }
      ]
    }
  },
  props: {
    title: {
      type: String,
      required: false
    },
    titleI18nKey: {
      type: String,
      required: false
    },
    noHead: {
      type: Boolean,
      default: false
    },
    noFilter: {
      type: Boolean,
      default: false
    },
    noHeadBtn: {
      type: Boolean,
      default: false
    },
    showCreateBtn: {
      type: Boolean,
      default: true
    },
    createBtnLabel: {
      type: String,
      required: false
    },
    hideOnClick: {
      type: Boolean,
      default: false
    },
    createBtnLabelI18nKey: {
      type: String,
      required: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    tableHeight: {
      default: null
    },
    listData: {
      type: Array,
      required: true
    },
    rowKey: {
      default: 'id'
    },
    rowClassName: {
      default: null
    },
    defaultExpandAll: {
      type: Boolean,
      default: false
    },
    expandRowKeys: {
      type: Array
    },
    treeProps: {
      type: Object,
      default () {
        return {
          hasChildren: 'hasChildren',
          children: 'children'
        }
      }
    },
    tableColumns: {
      type: Array,
      default () {
        return []
      }
    },
    noAction: {
      type: Boolean,
      default: false
    },
    showActionBtn: {
      type: Function,
      required: false
    },
    disabledActionBtn: {
      type: Function,
      required: false
    },
    showActionBtnTooltip: {
      type: Function,
      required: false
    },
    actionBtnTooltip: {
      type: Function,
      required: false
    },
    actionBtn: {
      type: Object,
      default: null
    },
    pagination: {
      type: Object,
      default: null
    },
    noPagination: {
      type: Boolean,
      default: false
    },
    dragElClass: {
      type: String,
      default: 'draggable-handle'
    },
    dragSortKey: {
      type: String,
      default: 'sort'
    },
    dragSortIcon: {
      type: String,
      default: 'el-icon-sort'
    },
    dragSortFunc: {
      type: Function,
      default: null
    },
    defaultSort: {
      required: false,
      default () {
        return {
          prop: null,
          order: null
        }
      }
    },
    showSearchFilter: {
      type: Boolean,
      default: false
    },
    spanMethod: {
      default: null
    },
    cellStyle: {
      type: Function,
      default: null
    },
    actionWidth: {
      type: String,
      default: '100px'
    }
  },
  data () {
    return {
      selectLabelI18n,
      defaultActionBtn: {
        edit: {
          show: true,
          labelI18nKey: 'common.btn.edit'
        },
        disable: {
          show: false,
          labelI18nKey: (row) => `common.btn.${row.status ? 'disable' : 'enable'}`
        },
        delete: {
          show: false,
          labelI18nKey: 'common.btn.delete'
        }
      }
    }
  },
  computed: {
    titleLabel () {
      return selectLabelI18n(this, 'title')
    },
    defaultCreateBtnLabel () {
      return selectLabelI18n(this, 'createBtnLabel', 'common.btn.add')
    },
    actionLabel () {
      return selectLabelI18n(this, 'actionColumnLabel', 'common.label.operate')
    },
    defaultActionBtnLabel () {
      return selectLabelI18n(this, 'actionBtnLabel', 'common.btn.action')
    },
    mergedActionBtn () {
      if (this.actionBtn) {
        const actionBtn = {}
        _.forEach(this.actionBtn, (btn, btnName) => {
          actionBtn[btnName] = btn
          const defaultBtn = this.defaultActionBtn[btnName]
          if (!defaultBtn) {
            return
          }
          this.mergeActionBtn(btn, defaultBtn)
        })
        return actionBtn
      } else {
        return this.defaultActionBtn
      }
    }
  },
  created () {
  },
  mounted () {
    const table = document.querySelector('.el-table__body-wrapper tbody')
    const tableSorter = Sortable.create(table, {
      dataIdAttr: 'class',
      handle: `.${this.dragElClass}`,
      onEnd: ({ newIndex, oldIndex }) => {
        if (this.dragSortFunc) {
          this.dragSortFunc(newIndex, oldIndex, tableSorter)
        } else {
          const originSortArray = this.listData.map((item) => item[this.dragSortKey])
          const targetRow = this.listData.splice(oldIndex, 1)[0]
          this.listData.splice(newIndex, 0, targetRow)
          this.listData.forEach((element, index) => {
            element[this.dragSortKey] = originSortArray[index]
          })
          this.$emit('dragSort', newIndex, oldIndex)
        }
      }
    })
  },
  methods: {
    handleSortChange ({ prop, order }) {
      this.$emit('sort-change', prop, order === 'descending' ? '-' : '')
    },
    handleSelectAll (selection) {
      this.$emit('select-all', selection)
    },
    handleSelectionChange (selection) {
      this.$emit('selection-change', selection)
    },
    handleExpandChange (row, expanded) {
      this.$emit('expand-change', { row, expanded })
    },
    mergeActionBtn (btn, defaultBtn) {
      if (!btn.label && !btn.labelI18nKey && (defaultBtn.label || defaultBtn.labelI18nKey)) {
        btn.label = defaultBtn.label
        btn.labelI18nKey = defaultBtn.labelI18nKey
      }
      if (btn.show === undefined && defaultBtn.show !== undefined) {
        btn.show = defaultBtn.show
      }
    },
    getColumnShowOverflowTooltip (column) {
      if (column.showOverflowTooltip !== undefined) {
        return column.showOverflowTooltip
      }
      return true
    },
    getColumnData (column, rowData, elColumn, index) {
      const data = _.get(rowData, column.dataKey)
      return column.formatNumber ? formatBigNumber(data) : data
    },
    getFormattedColumnData (column, rowData, elColumn, index) {
      let data = this.getColumnData(column, rowData, elColumn, index)
      if (column.formatter) {
        data = column.formatter(data, rowData, column, index, elColumn)
      }
      return data
    },
    getActionBtnShow (rowData, index) {
      if (this.showActionBtn) {
        return this.showActionBtn(rowData, index)
      } else {
        return _.some(this.mergedActionBtn, (btn, btnName) => this.getActionItemBtnShow(btn, btnName, rowData, index))
      }
    },
    getActionBtnDisabled (rowData, index) {
      if (this.disabledActionBtn) {
        return this.disabledActionBtn(rowData, index)
      }
      return false
    },
    getActionBtnTooltipShow (rowData, index) {
      if (this.getActionBtnTooltip(rowData, index)) {
        if (this.showActionBtnTooltip) {
          return this.showActionBtnTooltip(rowData, index)
        }
        return true
      }
      return false
    },
    getActionBtnTooltip (rowData, index) {
      if (this.actionBtnTooltip) {
        return this.actionBtnTooltip(rowData, index)
      }
    },
    getActionBtnLabel (btn, btnName, rowData, index) {
      return selectFuncLabelI18n([rowData, index], btn)
    },
    getActionItemBtnShow (btn, btnName, rowData, index) {
      let show = true
      if (typeof btn.show === 'function') {
        show = btn.show(rowData, index)
      } else if (btn.show !== undefined) {
        show = btn.show
      } else if (btn.children) {
        show = _.some(btn.children, (child, childName) => this.getActionItemBtnShow(child, childName, rowData, index, btn, btnName))
      }
      return show
    },
    handleAction ({ btn, btnName }, rowData, index) {
      if (this.getActionBtnDisabled(rowData, index)) {
        return
      }
      if (btn.onclick) {
        btn.onclick(rowData, index)
      } else {
        this.$emit('action', rowData, btn, btnName, index)
      }
    },
    handleCreate () {
      this.$emit('create')
    },
    handleSizeChange (pageSize) {
      this.pagination.page = 1
      this.pagination.pageSize = pageSize
      this.$emit('pagination-change', this.pagination)
    },
    handleCurrentChange (currentPage) {
      this.pagination.page = currentPage
      this.$emit('pagination-change', this.pagination)
    }
  }
}
</script>

<style lang="scss" scoped>
$headerHeight: 32px;

.list-page {
  .list-head {
    display: flex;
    height: $headerHeight;
    margin-bottom: 20px;
    align-items: center;

    .title {
      margin-left: 0;
      margin-right: auto;
      color: #303133;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
    }

    .filter-container {
      display: flex;
      align-items: center;
      margin-left: 24px;
      margin-right: 0;

      ::v-deep {
        > * {
          margin-right: 10px;
          vertical-align: middle;

          &:last-child {
            margin-right: 0;
          }
        }

        .el-input__inner {
          height: $headerHeight;
          line-height: $headerHeight;
          border-radius: 4px;
        }

        .el-input__icon {
          line-height: $headerHeight;
        }
      }
    }

    .btn-container {
      margin-left: 24px;
      margin-right: 0;

      ::v-deep {
        .el-button {
          padding: 6px 14px;
          border-radius: 4px;
          border-color: #0096cc;
          color: #0096cc;
          line-height: 18px;
        }
      }
    }
  }

  .search-filter {
    display: flex;
    margin-bottom: 20px;
    align-items: center;

    .search-filter-container {
      display: flex;
      align-items: center;
    }

    .search-filter-btn {
      display: flex;
      align-items: center;
      margin-left: 40px;
    }

    ::v-deep {
      .el-button {
        padding: 6px 14px;
        border-radius: 4px;
        border-color: #0096cc;
        color: #0096cc;
        line-height: 18px;
      }
    }
  }

  .list-container {
    .el-table {
      ::v-deep {
        .el-table__header {
          .has-gutter {
            tr {
              th {
                &:nth-last-child(2) {
                  border-top-right-radius: 6px;
                }
              }
            }
          }

          tr {
            th {
              padding: 11px 0;
              background: #fafafa;
              border-bottom: 0;

              &:first-child {
                border-top-left-radius: 6px;
              }

              &:last-child {
                border-top-right-radius: 6px;
              }

              .cell {
                font-weight: 500;
                word-break: break-word;
                word-wrap: break-word;
              }
            }
          }
        }

        .action-btn-column {

          .cell {
            text-overflow: initial;
          }
        }
      }

      .drag-sort-icon {
        cursor: pointer;
      }

      .action-dropdown {
        .dropdown-btn {
          padding: 6px 14px;
          font-size: 14px;
          line-height: 18px;
          border-color: #0096cc;
          color: #0096cc;
          font-weight: 500;
          border-radius: 4px;

          .dropdown-btn-icon {
            margin-left: 5px;
            transform: rotate(90deg);
          }

          &.is-disabled {
            color: #c0c4cc;
            border-color: #ebeef5;
          }
        }
      }
    }

    .el-pagination {
      padding: 24px 0 2px;
      text-align: right;

      ::v-deep {
        .el-input {
          &.el-input--suffix {
            &:hover {
              .el-input__inner {
                padding-right: 25px;
              }
            }
          }

          .el-input__inner {
            height: 28px;
            line-height: 28px;
            border-radius: 4px;
          }
        }
      }
    }
  }
}

.action-btn-item {
  &:hover {
    background: #f0f0f0;
  }
}
</style>
